<template>
    <div class="list">
        <div class="title-container">
            <span class="title">{{
                $t('reports.dashboards.dashboardsPanel')
            }}</span>
        </div>
        <Loader class="mt-4" :show="isLoading" />
        <div v-if="!isLoading" class="main-container">
            <div v-if="dashboards.length > 0" class="dashboards-container">
                <div
                    class="create-dashboard-box"
                    @click="onClickCreateDashboard"
                >
                    <i class="fas fa-plus" />
                </div>
                <div
                    :key="dashboard.id"
                    v-for="dashboard in dashboards"
                    class="dashboard-container"
                >
                    <div class="report-dashboard-box">
                        <b-dropdown
                            dropleft
                            no-caret
                            size="sm"
                            variant="white"
                            class="actions"
                        >
                            <template slot="button-content">
                                <i class="fas fa-ellipsis-v text-muted" />
                            </template>
                            <b-dropdown-item
                                @click="onClickDuplicateDashboard(dashboard.id)"
                            >
                                <div class="dropdown-item-container">
                                    <i class="fas fa-copy" />
                                    <span>{{
                                        $t('form.button.duplicate')
                                    }}</span>
                                </div>
                            </b-dropdown-item>
                            <b-dropdown-item
                                @click="onClickDeleteDashboard(dashboard.id)"
                            >
                                <div class="dropdown-item-container">
                                    <i class="far fa-trash-alt" />
                                    <span>{{ $t('form.button.delete') }}</span>
                                </div>
                            </b-dropdown-item>
                        </b-dropdown>
                        <div class="options">
                            <div
                                class="edit"
                                @click="onClickEditDashboard(dashboard.id)"
                            >
                                <i class="fas fa-pencil-alt mr-2" />
                                <span>{{ $t('form.button.edit') }}</span>
                            </div>
                            <div
                                class="view"
                                @click="onClickViewDashboard(dashboard.id)"
                            >
                                <i class="fas fa-eye mr-2" />
                                <span>{{ $t('form.button.show') }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="dashboard-title">{{ dashboard.title }}</div>
                </div>
            </div>
            <div
                v-if="dashboards.length == 0"
                class="without-dashboard-container"
            >
                <span class="text">{{
                    $t('reports.dashboards.withoutDashboards')
                }}</span>
                <b-button
                    class="create-dashboard-button"
                    @click="onClickCreateDashboard"
                    >{{ $t('reports.dashboards.createNewDashboard') }}</b-button
                >
                <img class="image" src="@/assets/images/report.svg" />
            </div>
        </div>
    </div>
</template>
<script>
import { Loader } from '@/views/reports/components'
import { success, danger } from '@/lib/notification'
import { proceedWithConfirmation } from '@/lib/sweet-alert'
import {
    getDashboards,
    deleteDashboard,
    duplicateDashboard,
} from '@/views/reports/api'
export default {
    components: {
        Loader,
    },
    mounted() {
        this.getDashboards()
    },
    data() {
        return {
            isLoading: false,
            dashboards: [],
        }
    },
    methods: {
        async getDashboards() {
            this.isLoading = true

            this.dashboards = await getDashboards({
                filter: 'id;title',
            })

            this.isLoading = false
        },
        onClickCreateDashboard() {
            this.$router.push('/v2/reports/new')
        },
        onClickViewDashboard(dashboardId) {
            this.$router.push(`/v2/reports/view/${dashboardId}`)
        },
        onClickEditDashboard(dashboardId) {
            this.$router.push(`/v2/reports/edit/${dashboardId}`)
        },
        onClickDuplicateDashboard(dashboardId) {
            proceedWithConfirmation({
                onConfirm: () => duplicateDashboard(dashboardId),
                callback: newDashboard => {
                    this.showSucessToast('duplicatedSuccessfully')
                    this.addDashboard(newDashboard)
                },
                onError: () => danger(),
            })
        },
        addDashboard(newDashboard) {
            this.dashboards = [...this.dashboards, newDashboard]
        },
        onClickDeleteDashboard(dashboardId) {
            proceedWithConfirmation({
                onConfirm: () => deleteDashboard(dashboardId),
                callback: () => {
                    this.showSucessToast('deletedSuccessfully')
                    this.removeDashboard(dashboardId)
                },
                onError: () => danger(),
            })
        },
        removeDashboard(dashboardId) {
            setTimeout(() => {
                this.dashboards = this.dashboards.filter(
                    d => d.id !== dashboardId
                )
            }, 400)
        },
        showSucessToast(notificationKey) {
            const path = `notification.${notificationKey}`
            const message = this.$t(path, { item: 'Dashboard' })
            success(message)
        },
    },
}
</script>
<style lang="scss" scoped>
$box-shadow-color: rgba(179, 181, 181, 0.5);

/deep/ .dropdown-item {
    padding: 5px 10px;
}

/deep/ .dropdown-toggle {
    box-shadow: none;
}

.dropdown-item-container {
    margin-left: 0.5rem;
}

.base-box {
    height: 300px;
    width: 300px;
    border-radius: 3px;
}

.list {
    .title-container {
        display: flex;
        align-items: center;
        height: 84px;
        font-family: Roboto;
        background-color: #fff;
        box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);

        .title {
            color: #6e6e6e;
            margin-left: 20px;
            font-weight: 700;
            font-size: 24px;
        }
    }

    .main-container {
        padding-right: 20px;
        padding-left: 20px;
        margin: 36px 28px;
        border-radius: 3px;
        background: #ffffff;
        border: 1px solid #bec0c0;
        animation-name: fadeIn;
        animation-duration: 0.3s;

        .dashboards-container {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            padding: 38px;

            .create-dashboard-box {
                @extend .base-box;

                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 100px;
                color: #022b4f;
                background: #ffffff;
                cursor: pointer;
                border: 3px solid #022b4f;
                margin-right: 40px;
                margin-bottom: 30px;
            }

            .dashboard-container {
                height: 350px;
                width: 300px;
                font-weight: 500;
                color: #6e6e6e;
                margin-right: 40px;
                margin-bottom: 40px;

                .dashboard-title {
                    padding: 0 12px 0 12px;
                    text-align: center;
                    text-overflow: ellipsis;
                }

                .report-dashboard-box {
                    @extend .base-box;
                    display: flex;
                    flex-direction: column;
                    position: relative;
                    border: 2px solid #b3b5b5;
                    border-radius: 5px;
                    box-shadow: inset 0 0 30px 1px $box-shadow-color;
                    background-image: url('~@/assets/images/chart.svg');
                    background-size: 100% 100%;
                    transition: 0.3s ease all;

                    &:hover {
                        box-shadow: none;

                        .options {
                            display: flex;
                            opacity: 1;
                        }
                    }

                    .options {
                        display: none;
                        opacity: 0;
                        justify-content: center;
                        align-items: center;
                        margin-top: auto;
                        text-align: center;
                        cursor: pointer;
                        height: 40px;
                        background-color: white;
                        box-shadow: 0px -5px 10px $box-shadow-color;
                        animation-name: fadeIn;
                        animation-duration: 0.3s;

                        .edit {
                            border-right: 2px solid #b3b5b5;
                        }

                        .edit,
                        .view {
                            padding: 10px;
                            font-size: 13px;
                            width: 50%;
                        }
                    }

                    .actions {
                        align-self: flex-end;
                        margin-top: 10px;
                    }
                }
            }
        }

        .without-dashboard-container {
            display: flex;
            flex-direction: column;
            align-items: center;

            .text {
                margin-top: 55px;
                margin-bottom: 25px;
                font-size: 16px;
            }

            .create-dashboard-button {
                color: #ffff;
                background: #022b4f;
                margin-bottom: 50px;
            }

            .image {
                max-width: 533px;
                max-height: 340px;
                margin-bottom: 25px;
            }
        }
    }
}
</style>
